import { useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { Mail, Loader2, CheckCircle2, Sparkles } from "lucide-react";

/**
 * Site footer with newsletter capture. Newsletter form POSTs to
 * /api/subscribers (idempotent). Social links are intentionally omitted
 * until real profiles exist — re-add them here when accounts are live.
 */
export default function Footer() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    setMsg("");
    try {
      const { data } = await http.post("/subscribers", { email: email.trim() });
      setState("success");
      setMsg(
        data?.new
          ? "You're on the list. Welcome to the Empire."
          : "You were already on the list — thanks for coming back!",
      );
      setEmail("");
    } catch (err) {
      setState("error");
      setMsg(formatApiError(err?.response?.data?.detail) || "Could not subscribe. Try again.");
    }
  };

  return (
    <footer data-testid="site-footer" className="mt-32 border-t border-[var(--line-strong)]">
      {/* Newsletter — spec headline: "Never Miss a Price Increase" */}
      <div className="border-b border-[var(--line-strong)]">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neon-cyan">
              <Sparkles className="w-3.5 h-3.5" /> Newsletter
            </div>
            <h2
              className="mt-3 font-display text-2xl sm:text-3xl leading-tight text-white"
              data-testid="newsletter-headline"
            >
              Never Miss a Price Increase
            </h2>
            <p className="mt-2 text-sm text-[var(--fg-muted)] max-w-md">
              Join our mailing list for launch updates, new features, and important announcements.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-3"
            data-testid="newsletter-form"
            noValidate
          >
            <div className="relative flex-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-muted)]" />
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourstartup.com"
                className="input-lux pl-9 w-full"
                disabled={state === "busy"}
              />
            </div>
            <button
              type="submit"
              disabled={state === "busy"}
              data-testid="newsletter-submit"
              className="btn-primary justify-center whitespace-nowrap"
            >
              {state === "busy" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Subscribing…
                </>
              ) : (
                <>Subscribe</>
              )}
            </button>
          </form>
        </div>
        {msg && (
          <div
            className={`max-w-6xl mx-auto px-6 pb-6 text-sm flex items-center gap-2 ${
              state === "success" ? "text-emerald-300" : "text-red-400"
            }`}
            data-testid="newsletter-status"
          >
            {state === "success" && <CheckCircle2 className="w-4 h-4" />}
            {msg}
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 font-semibold text-lg">
            <span className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 via-cyan-400 to-fuchsia-500" />
            PixelEmpire <span className="text-neon-cyan text-xs">AI</span>
          </div>
          <p className="mt-3 text-sm text-[var(--fg-muted)] max-w-xs">
            10,000 units of permanent digital real estate. Claim your permanent spot on the
            digital grid for $0.50 per pixel — get a lifetime SEO backlink.
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)] mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><a href="/#explore" className="hover:text-neon-cyan">Live grid</a></li>
            <li><Link to="/pricing" className="hover:text-neon-cyan">Pricing</Link></li>
            <li><Link to="/how-it-works" className="hover:text-neon-cyan">How it works</Link></li>
            <li><Link to="/directory" className="hover:text-neon-cyan" data-testid="footer-directory-link">Directory</Link></li>
            <li><Link to="/dashboard" className="hover:text-neon-cyan">Dashboard</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)] mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-neon-cyan">About</Link></li>
            <li><Link to="/blog" className="hover:text-neon-cyan">Blog</Link></li>
            <li><Link to="/faq" className="hover:text-neon-cyan">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-neon-cyan">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)] mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-neon-cyan">Terms of service</Link></li>
            <li><Link to="/privacy" className="hover:text-neon-cyan">Privacy policy</Link></li>
            <li><Link to="/terms#refunds" className="hover:text-neon-cyan">Refund policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[var(--line-strong)] py-6 text-xs text-center text-[var(--fg-muted)]">
        © {new Date().getFullYear()} PixelEmpire AI. All rights reserved.
      </div>
    </footer>
  );
}
